import { requireDashboardOrigin } from "@playbrain/auth/server/dashboardOriginGuard"
import { requireDashboardAuth } from "@playbrain/auth/server/requireDashboardAuth"
import { logEvent } from "@playbrain/config"
import { contentTypeNameSchema } from "@playbrain/content-types"
import { dashboardApiErrorMessage } from "@server/dashboard-api-errors"
import { serverRouteConfig } from "@server/server-route-config"
import { createFileRoute } from "@tanstack/react-router"
import { createServerOnlyFn } from "@tanstack/react-start"
import * as z from "zod"

const presignInputSchema = z.object({
  contentType: contentTypeNameSchema,
  fileName: z.string().min(1).max(180),
  mimeType: z.string().min(1).max(100),
  size: z.number().int().positive(),
})

const profileImagePresignInputSchema = z.object({
  purpose: z.literal("profileImage"),
  fileName: z.string().min(1).max(180),
  mimeType: z.string().min(1).max(100),
  size: z.number().int().positive(),
})

const documentPresignInputSchema = z.object({
  purpose: z.literal("document"),
  contentType: contentTypeNameSchema,
  fileName: z.string().min(1).max(180),
  mimeType: z.string().min(1).max(100),
  size: z.number().int().positive(),
})

const dashboardAssetPresignInputSchema = z.union([
  profileImagePresignInputSchema,
  documentPresignInputSchema,
  presignInputSchema,
])

export const Route = createFileRoute("/api/dashboard/assets/presign")(
  serverRouteConfig({
    server: {
      handlers: {
        POST: async ({ request }) => {
          const originError = requireDashboardOrigin(request)
          if (originError) return originError

          try {
            const input = dashboardAssetPresignInputSchema.parse(await request.json())
            if ("purpose" in input && input.purpose === "profileImage") {
              const auth = await requireDashboardAuth(request, [])
              if ("response" in auth) return auth.response
              const { createDashboardProfileImageUploadData } = await import(
                "@api/features/content-assets/dashboardAssets"
              )
              const userId = auth.user.sub ?? auth.user.email ?? "dashboard-user"
              return Response.json(await createDashboardProfileImageUploadData({ ...input, userId }))
            }

            const auth = await requireDashboardAuth(request, [])
            if ("response" in auth) return auth.response
            try {
              await requireDashboardAssetUploadPermission(auth.user)
            } catch (error) {
              if (error instanceof Error && error.message === "PERMISSION DENIED") {
                logEvent("warn", "dashboard_assets.presign_denied", {
                  userId: dashboardUserId(auth.user) ?? "unknown",
                })
                return Response.json(
                  {
                    error:
                      "You don't have permission to upload dashboard assets. Please contact an administrator.",
                  },
                  { status: 403 }
                )
              }
              throw error
            }
            // A document lands in the same bucket under the same permission,
            // so it clears the same gate above before branching here.
            if ("purpose" in input && input.purpose === "document") {
              const { createDashboardDocumentUploadData } = await import(
                "@api/features/content-assets/dashboardAssets"
              )
              const { purpose: _purpose, ...documentInput } = input
              return Response.json(await createDashboardDocumentUploadData(documentInput))
            }

            const contentInput = presignInputSchema.parse(input)
            const { createDashboardAssetUploadData } = await import(
              "@api/features/content-assets/dashboardAssets"
            )
            return Response.json(await createDashboardAssetUploadData(contentInput))
          } catch (error) {
            logEvent("error", "dashboard_assets.presign_failed", {
              reason: error instanceof Error ? error.message : String(error),
            })
            return Response.json(
              { error: dashboardApiErrorMessage(error, "Could not create upload URL.") },
              { status: 400 }
            )
          }
        },
      },
    },
  })
)

const requireDashboardAssetUploadPermission = createServerOnlyFn(
  async (user: {
    id?: unknown
    sub?: unknown
    role?: unknown
    user_metadata?: { role?: unknown; roles?: unknown }
  }) => {
    const { resolveDashboardUserRole } = await import(
      "@dashboard/components/users/dashboard-current-user-role"
    )
    const { assertSubjectCanUsePermission } = await import(
      "@api-users/features/admin-users/adminRolesService"
    )
    await assertSubjectCanUsePermission(
      { userId: dashboardUserId(user), role: await resolveDashboardUserRole(user) },
      "playbrain.website.assets",
      "create"
    )
  }
)

function dashboardUserId(user: { id?: unknown; sub?: unknown }) {
  if (typeof user.sub === "string" && user.sub) return user.sub
  if (typeof user.id === "string" && user.id) return user.id
  return null
}
